import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useCipherStore } from './store/cipherStore';
import CipherInput from './components/CipherInput';
import CipherOutput from './components/CipherOutput';
import CipherManager from './components/CipherManager';

function App() {
  const { darkMode, setDarkMode, loadFromStorage } = useCipherStore();
  
  useEffect(() => {
    // Load saved state from localStorage on app start
    loadFromStorage();
  }, [loadFromStorage]);
  
  useEffect(() => {
    // Apply dark mode class to document
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);
  
  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  return (
    <div className={`min-h-screen transition-all duration-500 ${
      darkMode 
        ? 'bg-gray-900 text-gray-100' 
        : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Background Grid */}
      <div className="fixed inset-0 cyber-grid opacity-20 pointer-events-none"></div>
      
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 border-b border-neon-cyan/20 bg-gray-900/80 backdrop-blur-sm"
      >
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-4"
            >
              <div className="text-4xl">🎨</div>
              <div>
                <h1 className="text-3xl font-bold text-neon-cyan glow-text">
                  CipherCanvas
                </h1>
                <p className="text-sm text-gray-400">
                  Visual Encryption Playground
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTheme}
                className="cyber-button rounded-lg p-3"
                title={`Switch to ${darkMode ? 'light' : 'dark'} mode`}
              >
                {darkMode ? '☀️' : '🌙'}
              </motion.button>
              
              <div className="text-sm text-gray-400">
                <div>100% Client-Side</div>
                <div className="text-xs">No API Required</div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.header>
      
      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Input/Output */}
          <div className="space-y-8">
            <CipherInput />
            <CipherOutput />
          </div>
          
          {/* Right Column - Cipher Manager */}
          <div>
            <CipherManager />
          </div>
        </div>
        
        {/* Features Section */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 cyber-border rounded-lg p-8"
        >
          <h2 className="text-2xl font-bold text-neon-cyan glow-text mb-6 text-center">
            Features
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: '🔐',
                title: 'Multiple Ciphers',
                description: 'Caesar, ROT13, Base64, XOR, and Custom ciphers'
              },
              {
                icon: '🎨',
                title: 'Visual Layers',
                description: 'Stack multiple encryption layers with visual feedback'
              },
              {
                icon: '⚡',
                title: 'Real-time Processing',
                description: 'Instant encryption and decryption as you type'
              },
              {
                icon: '💾',
                title: 'Export & Save',
                description: 'Export results and save configurations locally'
              },
              {
                icon: '🌐',
                title: 'Offline Ready',
                description: 'Works completely offline, no internet required'
              },
              {
                icon: '📱',
                title: 'Responsive Design',
                description: 'Works perfectly on desktop, tablet, and mobile'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="cyber-border rounded-lg p-4 text-center hover:border-neon-cyan/50 transition-colors"
              >
                <div className="text-3xl mb-3">{feature.icon}</div>
                <h3 className="font-semibold text-neon-cyan mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </main>
      
      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="relative z-10 border-t border-neon-cyan/20 bg-gray-900/80 backdrop-blur-sm mt-16"
      >
        <div className="container mx-auto px-4 py-6 text-center text-gray-400">
          <p className="text-sm">
            Built with React, Tailwind CSS, Zustand, and Framer Motion
          </p>
          <p className="text-xs mt-2">
            CipherCanvas - A Visual Encryption Playground © 2024
          </p>
        </div>
      </motion.footer>
    </div>
  );
}

export default App;
